import { OkDialog } from '@/components/molecules';
import { useAuth } from '@/core/contexts/AuthContext';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useTranslation } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { logger } from '@/core/logger';
import { passcodeStorage } from '@/core/storage/passcodeStorage';
import { useAppStore } from '@/core/stores/appStore';
import { isNativeAdhaFlowActive } from '@/modules/auth/services/adhaUaePassNative';
import { trackUaePassLoginCancelled, trackUaePassLoginError } from '@/modules/auth/hooks/useLogin';
import { UAEPassAuth } from '@/modules/onboarding/identity/uae-pass/components/UAEPassAuth';
import type { UAEPassAuthResult } from '@/modules/onboarding/identity/uae-pass/types';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';

export default function UAEPassScreen() {
    const setUaePassRegistration = useAppStore((state) => state.setUaePassRegistration);
    const { login: authLogin } = useAuth();
    const { t } = useTranslation();
    const setAuth = useAppStore((state) => state.setAuth);
    const [errorVisible, setErrorVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const oauthParams = useLocalSearchParams<{
        url?: string;
        code?: string;
        accessCode?: string;
        access_code?: string;
        error?: string;
        state?: string;
        error_description?: string;
    }>();
    const [adhaHandoff] = useState(
        () => Platform.OS !== 'web' && isNativeAdhaFlowActive(),
    );

    const goBackFromUaePass = useCallback(() => {
        trackUaePassLoginCancelled();
        if (router.canGoBack()) {
            router.back();
        } else {
            router.replace('/welcome');
        }
    }, []);

    const handleError = useCallback((error: string) => {
        // Same as iOS: any API / UAE Pass failure → OkDialog, then back on OK.
        logger.warn('UAEPassAuthScreen', 'UAE Pass auth error', { error });
        const raw = error || '';
        const isTechnical =
            raw === 'ANDROID_HTTP_400' ||
            raw.includes('400') ||
            raw.toLowerCase().includes('status code') ||
            raw.toLowerCase().includes('axios');
        const displayMessage = isTechnical ? t('errors.registrationFailed') : (raw || t('errors.registrationFailed'));
        trackUaePassLoginError(displayMessage);
        setErrorMessage(displayMessage);
        setErrorVisible(true);
    }, [t]);

    useEffect(() => {
        if (!adhaHandoff) {
            if (Platform.OS === 'android' && oauthParams.error) {
                const isCancel = oauthParams.error.toLowerCase().includes('cancel');
                if (isCancel) {
                    goBackFromUaePass();
                } else {
                    handleError(oauthParams.error_description || oauthParams.error);
                }
            }
            return;
        }
        router.replace({
            pathname: '/uae-pass',
            params: {
                ...(oauthParams.url ? { url: oauthParams.url } : {}),
                ...(oauthParams.code ? { code: oauthParams.code } : {}),
                ...(oauthParams.accessCode ? { accessCode: oauthParams.accessCode } : {}),
                ...(oauthParams.access_code ? { access_code: oauthParams.access_code } : {}),
                ...(oauthParams.error ? { error: oauthParams.error } : {}),
                ...(oauthParams.state ? { state: oauthParams.state } : {}),
                ...(oauthParams.error_description
                    ? { error_description: oauthParams.error_description }
                    : {}),
            },
        });
    }, [adhaHandoff, oauthParams, goBackFromUaePass, handleError]);

    const handleSuccess = async (result: UAEPassAuthResult) => {
        // Check if user has JWT token (existing user)
        if (result.token) {
            try {
                logger.info('UAEPassAuthScreen', 'Existing user detected, logging in');

                // Extract user data from UAE Pass result
                const user = result.user ? {
                    ...result.user, // Include all user fields first
                    id: result.user.idn || result.user.uuid || 'unknown',
                    primary_email: result.user.email || '',
                } : {
                    id: 'unknown',
                    primary_email: '',
                };

                // Check if this is a different user (new user login) based on stored auth user
                const previousUser = useAppStore.getState().auth.user;
                const isDifferentUser = !!previousUser && previousUser.id !== user.id;

                if (isDifferentUser) {
                    // Different user - clear passcode state for old user
                    logger.info('UAEPassAuthScreen:Different user detected, clearing passcode', {
                        previousUserId: previousUser?.id,
                        newUserId: user.id,
                    });
                    await passcodeStorage.clearPasscodeState();
                    logger.info('UAEPassAuthScreen:Passcode cleared for new UAE Pass user');
                }

                // Save to appStore (includes persistence)
                setAuth(result.token, user);

                trackCatalogEvent({
                    screen: DT_SCREEN.UAE_PASS,
                    actionName: 'uae_pass_login_success',
                    eventType: 'action',
                });

                // Update global auth state
                authLogin();

                // Check if first-time login and passcode not set
                const isFirstTimeLogin = result.details?.first_time_login === true;
                const isPasscodeSetInResponse = result.details?.passcode !== null && result.details?.passcode !== undefined;

                if (isFirstTimeLogin && !isPasscodeSetInResponse) {
                    // First-time login - navigate to passcode setup (no password step for UAE Pass)
                    logger.info('UAEPassAuthScreen', 'First-time login detected, navigating to passcode setup');
                    try {
                        if (router.canGoBack()) {
                            router.dismissAll();
                        }
                    } catch (error) {
                        // Ignore dismissAll errors - replace will handle navigation
                        logger.debug('UAEPassScreen', 'dismissAll failed, using replace only', error);
                    }
                    router.replace('/passcode?from=login&mode=setup' as any);
                } else {
                    // Existing user or passcode already set - navigate to home
                    // Dismiss all screens and redirect to home
                    // Use replace instead of dismissAll + replace to avoid navigation errors
                    try {
                        if (router.canGoBack()) {
                            router.dismissAll();
                        }
                    } catch (error) {
                        // Ignore dismissAll errors - replace will handle navigation
                        logger.debug('UAEPassScreen', 'dismissAll failed, using replace only', error);
                    }
                    router.replace('/(auth)/(home)');
                }

                logger.info('UAEPassAuthScreen', 'Successfully logged in existing user');
            } catch (error) {
                logger.error('UAEPassAuthScreen', 'Failed to login existing user', error);
                setErrorMessage(t('auth.loginError'));
                setErrorVisible(true);
            }
        } else {
            // New user - continue with registration flow
            logger.info('UAEPassAuthScreen', 'New user detected, proceeding with registration');

            // Extract registration ID and customer ID from callback response if available
            // The callback response structure: { details: { customer_details: { registration_id }, uaepass_details: {...} } }
            const registrationId = (result as any)?.details?.customer_details?.registration_id || null;
            const customerId = (result as any)?.details?.customer_details?.customer_id || null;

            if (result.user) {
                // Store UAE Pass user data in appStore (without documents)
                setUaePassRegistration({
                    user: result.user,
                    registrationId,
                    customerId,
                });

                // Also save registration IDs to AsyncStorage for immediate access on app restart
                if (registrationId && customerId && result.user.idn && result.user.nationalityEN) {
                    const { RegistrationStorage } = await import('@/modules/onboarding/registration/storage/registrationStorage');
                    await RegistrationStorage.setRegistrationIds({
                        registrationId,
                        customerId,
                        idn: result.user.idn,
                        nationality: result.user.nationalityEN,
                    }).catch((error) => {
                        logger.error('UAEPassAuthScreen', 'Failed to save registration IDs to storage', error);
                    });
                }
            }

            router.replace('/uae-pass-details');
        }
    };

    if (adhaHandoff || (Platform.OS === 'android' && oauthParams.error)) {
        return null;
    }

    const initialCode = oauthParams.code || oauthParams.accessCode || oauthParams.access_code || null;
    const initialState = oauthParams.state || null;
    const initialUrl = oauthParams.url ? decodeURIComponent(oauthParams.url) : null;

    return (
        <>
            <UAEPassAuth
                onAuthSuccess={handleSuccess}
                onAuthError={handleError}
                onClose={goBackFromUaePass}
                hideWebView={errorVisible}
                initialCode={initialCode}
                initialState={initialState}
                initialUrl={initialUrl}
            />
            <OkDialog
                visible={errorVisible}
                message={errorMessage || t('auth.loginError')}
                onOk={() => {
                    setErrorVisible(false);
                    goBackFromUaePass();
                }}
            />
        </>
    );
}
